import axios, { AxiosInstance } from "axios";
import {
  Address,
  ApiKey,
  CreatedApiKey,
  Pricing,
  DailyDriverReport,
  DailyProgress,
  DailyReport,
  DashboardOrderStats,
  DashboardScheduleStats,
  Delivery,
  DriverV2,
  DriversSchedule,
  Dumpster,
  DumpsterLocationHistory,
  DumpYard,
  DumpYardTemplate,
  DumpYardTemplateType,
  DVIRQuestion,
  DVIRTemplate,
  DVIRReport,
  AcceptResult,
  FleetOptimization,
  FleetOptimizationInput,
  FleetOptimizationStatus,
  HGEOwesReport,
  SmartRoofOwesReport,
  Invoice,
  InvoiceStatus,
  OrderDetailsInfo,
  OrderProfit,
  OrdersDashboard,
  OwesReport,
  PayPeriodResponse,
  PayPeriodSummaryResponse,
  PickUp,
  ProfitReport,
  PunchCard,
  PunchCardsForAllDrivers,
  PunchCardsForDriver,
  DriverSafetySummary,
  FleetSafetySummaryResponse,
  TeamSafetySummary,
  Schedule,
  ScheduleDay,
  ScheduleItem,
  ServiceTimePerStop,
  DumpWeightByMaterial,
  User,
  Vehicle,
  YearlyReport,
  Customer,
  DumpEvent,
  DumpEventFilters,
  DumpEventUpdate,
  Receipt,
  ReceiptFilters,
  ReceiptUpdate,
  InvoiceSuggestion,
  SuggestionStatus,
  ApproveSuggestionBody,
  RangeSummary,
  Conversation,
  ConversationSummary,
  KnowledgeNote,
  KnowledgeNoteStatus,
  ChatAction,
  ClosedDay,
  DriverDocument,
} from "../../models";

import {
  TriplePInterface,
  CreateUserRequest,
  TriplePPagination,
  ScheduleForm,
  ScheduleItemForm,
  DumpYardTemplateForm,
  OrderFilters,
  UpdateOrderFormModel,
  CreateOrderParams,
  ApproveOrderParams,
  RejectOrderParams,
  GetDumpYardsParams,
  AddPickupInstructionsParams,
  SendConfirmationEmailParams,
  GetInvoicesParams,
  GetPunchCardSummariesParams,
  GetPunchCardsForDriverParams,
  UpdatePunchCardsParams,
  CreatePunchCardParams,
  ChangeDeliveryPriorityParams,
  ChangePickupPriorityParams,
  UpdateDumpYardInstructionsParams,
  MarkPaidParams,
  GetProfitReportParams,
  GetDailyDriverReportParams,
  CreateWeightOverageInvoiceParams,
  CreateCustomInvoiceParams,
  GetHGEOwesReportParams,
  GetSmartRoofOwesReportParams,
  BulkMarkPaidParams,
  CancelInvoiceParams,
  UpdatePaymentTypeParams,
  GetDumpstersParams,
  DumpsterForm,
  VehicleForm,
  GetDVIRQuestionsParams,
  DVIRQuestionForm,
  DVIRTemplateForm,
  AddQuestionToTemplateParams,
  RemoveQuestionFromTemplateParams,
  ChangeQuestionOrderParams,
  SetVehicleParams,
  CreateDriverRequest,
  UpdateDriverRequest,
  GetDVIRReportsParams,
  CreateDVIRReportParams,
  SetDumpsterLocationParams,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  UploadDriverDocumentRequest,
} from "./TriplePInterface";
import * as normalizers from "./normalizers";
import * as denormalizers from "./denormalizers";
import { LOCAL_STORAGE_KEYS } from "../../lib/localStorage";
import { Order } from "../../models/Order";
import { ScheduleComparison } from "../../models/ScheduleComparison";

export interface TriplePServiceDatasourceConfig {
  baseURL: string;
}

export class TriplePServiceDatasource implements TriplePInterface {
  private client: AxiosInstance;

  constructor(config: TriplePServiceDatasourceConfig) {
    const { baseURL } = config;

    this.client = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.client.interceptors.request.use((config) => {
      const jwtToken = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN);

      if (!!jwtToken && !!config.headers) {
        config.headers.Authorization = `Bearer ${jwtToken}`;
      }

      return config;
    });
  }

  async createUser(payload: CreateUserRequest): Promise<string> {
    const { data } = await this.client.post("/users", payload);
    return data.user_id;
  }

  async getProfile(): Promise<User> {
    const { data } = await this.client.get("/users/profile");
    return normalizers.getUserProfile(data);
  }

  // Drivers v2
  async getDriversV2(): Promise<DriverV2[]> {
    const { data } = await this.client.get("/drivers");
    return normalizers.getDriversNew(data);
  }

  async getDriverV2(id: string, withLocation = false): Promise<DriverV2> {
    const url = withLocation
      ? `/drivers/${id}?with_location=true`
      : `/drivers/${id}`;
    const { data } = await this.client.get(url);
    return normalizers.getDriverNew(data);
  }

  async createDriverV2(request: CreateDriverRequest): Promise<DriverV2> {
    const payload = {
      email: request.email,
      address: request.address,
      zip: request.zip,
      dl_expiration: request.dlExpiration,
      dot_physical_expiration: request.dotPhysicalExpiration,
      start_time: request.startTime,
    };
    const { data } = await this.client.post("/drivers", payload);
    return normalizers.getDriverNew(data);
  }

  async updateDriverV2(id: string, request: UpdateDriverRequest): Promise<DriverV2> {
    const payload: any = {};
    if (request.address !== undefined) payload.address = request.address;
    if (request.zip !== undefined) payload.zip = request.zip;
    if (request.dlExpiration !== undefined) payload.dl_expiration = request.dlExpiration;
    if (request.dotPhysicalExpiration !== undefined) payload.dot_physical_expiration = request.dotPhysicalExpiration;
    if (request.startTime !== undefined) payload.start_time = request.startTime;

    const { data } = await this.client.put(`/drivers/${id}`, payload);
    return normalizers.getDriverNew(data);
  }

  async deleteDriverV2(id: string): Promise<string> {
    await this.client.delete(`/drivers/${id}`);
    return id;
  }

  async createOrder(params: CreateOrderParams): Promise<Order> {
    const payload = denormalizers.toOrder(params);
    const { data } = await this.client.post("/orders", payload);

    return normalizers.getOrder(data.data);
  }

  async cancelOrder(orderId: string): Promise<string> {
    await this.client.delete(`/orders/${orderId}`);

    return orderId;
  }

  async updateOrder(form: UpdateOrderFormModel): Promise<Order> {
    const payload = denormalizers.toOrderFromUpdate(form);
    const { data } = await this.client.put(`/orders/${form.id}`, payload);

    return normalizers.getOrder(data.data);
  }

  async getOrders(
    filters: OrderFilters,
    pagination: TriplePPagination
  ): Promise<Order[]> {
    const params = new URLSearchParams({
      count: pagination.count + "",
      offset: pagination.offset + "",
    });

    if (filters.statuses.length > 0) {
      filters.statuses.forEach((status) => params.append("status", status));
    }
    if (!!filters.createdStartDate) {
      params.append("created_start_date", filters.createdStartDate);
    }
    if (!!filters.createdEndDate) {
      params.append("created_end_date", filters.createdEndDate);
    }
    if (!!filters.pickupStartDate) {
      params.append("pickup_start_date", filters.pickupStartDate);
    }
    if (!!filters.pickupEndDate) {
      params.append("pickup_end_date", filters.pickupEndDate);
    }
    if (!!filters.userId) {
      params.append("user_id", filters.userId);
    }
    if (!!filters.sort) {
      params.append("sort", filters.sort);
    }

    const { data } = await this.client.get(`/orders?${params.toString()}`);

    return normalizers.getOrders(data.data);
  }

  async getOrder(id: string): Promise<Order> {
    const { data } = await this.client.get(`/orders/${id}`);

    return normalizers.getOrder(data.data);
  }

  async getOrderDetails(id: string): Promise<OrderDetailsInfo> {
    const { data } = await this.client.get(`/orders/${id}/details`);

    const normalizeAction = (action: any) =>
      action
        ? {
            driverName: action.driver_name,
            completedAt: action.completed_at,
            status: action.status,
            delivery_id: action.delivery_id,
            pickup_id: action.pickup_id,
            photo_s3_location: action.photo_s3_location,
          }
        : undefined;

    return {
      delivery: normalizeAction(data.delivery),
      pickup: normalizeAction(data.pickup),
    };
  }

  // svc-037/048 GET /orders/{id}/profit — a flat JSON struct (not JSON:API). Money
  // fields arrive as full-precision decimal strings; parse to numbers here.
  // revenue_breakdown is a per-invoice list (svc-048): revenue = sum of amounts.
  async getOrderProfit(id: string): Promise<OrderProfit> {
    const { data } = await this.client.get(`/orders/${id}/profit`);
    const n = (v: any) => (v == null ? 0 : parseFloat(v));
    return {
      revenue: n(data.revenue),
      revenueBreakdown: {
        invoices: (data.revenue_breakdown?.invoices ?? []).map((inv: any) => ({
          type: inv.type ?? "",
          amount: n(inv.amount),
          status: inv.status ?? "",
        })),
      },
      legs: (data.legs ?? []).map((l: any) => ({
        kind: l.kind,
        from: l.from ?? "",
        to: l.to ?? "",
        seconds: l.seconds ?? 0,
        miles: l.miles ?? 0,
        labor: n(l.labor),
        fuel: n(l.fuel),
        annotations: l.annotations ?? [],
      })),
      dumpCost: n(data.dump_cost),
      dumpEvents: (data.dump_events ?? []).map((d: any) => ({
        paidAmount: n(d.paid_amount),
        extractionStatus: d.extraction_status,
      })),
      cost: n(data.cost),
      profit: n(data.profit),
      marginPct: n(data.margin_pct),
      status: data.status,
      annotations: data.annotations ?? [],
    };
  }

  async getDeliveries(date: string): Promise<Delivery[]> {
    const { data } = await this.client.get(
      `/orders/deliveries?delivery_date=${date}`
    );

    return normalizers.getDeliveries(data.data);
  }

  async getPickups(date: string): Promise<PickUp[]> {
    const { data } = await this.client.get(
      `/orders/pickups?pickup_date=${date}`
    );

    return normalizers.getPickups(data.data);
  }

  async getInvoice(id: string): Promise<string> {
    const { data } = await this.client.get(`/orders/${id}/invoice`);

    return data;
  }

  async approveOrder(params: ApproveOrderParams): Promise<Order> {
    const payload = denormalizers.toApproveOrderPayload(params);
    const { data } = await this.client.post(
      `/orders/${params.id}/approve`,
      payload
    );

    return normalizers.getOrder(data.data);
  }

  async getOrdersDashboard(): Promise<OrdersDashboard> {
    const { data } = await this.client.get("/orders/dashboard");

    return normalizers.getOrdersDashboard(data);
  }

  async getPickupInstructions(orderId: string): Promise<PickUp> {
    const { data } = await this.client.get(
      `/orders/${orderId}/pickup_instructions`
    );

    return normalizers.getPickup(data.data);
  }

  async addPickupInstructions(
    params: AddPickupInstructionsParams
  ): Promise<string> {
    const payload = { instructions: params.intructions };
    await this.client.post(`/orders/${params.id}/pickup_instructions`, payload);
    return params.id;
  }

  async rejectOrder(params: RejectOrderParams): Promise<Order> {
    const { data } = await this.client.post(`/orders/${params.id}/reject`);

    return normalizers.getOrder(data.data);
  }

  async sendConfirmationEmail(
    params: SendConfirmationEmailParams
  ): Promise<string> {
    await this.client.post(`/orders/${params.id}/confirmation_email`);

    return params.id;
  }

  async changeDeliveryPriority(
    params: ChangeDeliveryPriorityParams
  ): Promise<Delivery> {
    const payload = {
      priority: params.priority,
    };

    const { data } = await this.client.post(
      `/orders/${params.id}/change_delivery_priority`,
      payload
    );

    return normalizers.getDelivery(data.data);
  }

  async changePickupPriority(
    params: ChangePickupPriorityParams
  ): Promise<PickUp> {
    const payload = {
      priority: params.priority,
    };

    const { data } = await this.client.post(
      `/orders/${params.id}/change_pickup_priority`,
      payload
    );

    return normalizers.getPickup(data.data);
  }

  async updatePaymentType(params: UpdatePaymentTypeParams): Promise<Order> {
    const payload = {
      payment_type: params.paymentType,
    };
    const { data } = await this.client.post(
      `/orders/${params.id}/update_payment_type`,
      payload
    );

    return normalizers.getOrder(data.data);
  }

  async getDeliveryPhoto(deliveryId: string): Promise<string> {
    const { data } = await this.client.get(
      `/orders/deliveries/${deliveryId}/photo`
    );
    return data;
  }

  async getPickupPhoto(pickupId: string): Promise<string> {
    const { data } = await this.client.get(
      `/orders/pickups/${pickupId}/photo`
    );
    return data;
  }

  async createSchedule(form: ScheduleForm): Promise<Schedule> {
    const payload = denormalizers.toSchedule(form);
    const { data } = await this.client.post("/schedules", payload);

    return normalizers.getSchedule(data.data, []);
  }

  async getSchedules(date: string, driverId?: string): Promise<Schedule[]> {
    const { data } = await this.client.get(
      `/schedules?date=${date}${driverId ? `&driver_id=${driverId}` : ""}`
    );

    return normalizers.getSchedules(data);
  }

  async getSchedule(id: string): Promise<Schedule> {
    const { data } = await this.client.get(`/schedules/${id}`);

    return normalizers.getSchedule(data.data, data.included);
  }

  async deleteSchedule(id: string): Promise<string> {
    await this.client.delete(`/schedules/${id}`);

    return id;
  }

  async optimizeSchedule(
    id: string,
    settings?: { storageYardIds: string[]; startingDumpsterSize?: string; startingDumpsterFull?: boolean }
  ): Promise<Schedule> {
    const optimizationSettings: Record<string, any> = {};

    if (settings?.storageYardIds?.length) {
      optimizationSettings.storage_yard_ids = settings.storageYardIds;
    }

    if (settings?.startingDumpsterSize) {
      optimizationSettings.starting_dumpster_size = settings.startingDumpsterSize;
      if (settings.startingDumpsterFull !== undefined) {
        optimizationSettings.starting_dumpster_full = settings.startingDumpsterFull;
      }
    }

    const body = Object.keys(optimizationSettings).length
      ? { optimization_settings: optimizationSettings }
      : {};

    const { data } = await this.client.post(`/schedules/${id}/optimize`, body);

    return normalizers.getSchedule(data.data, []);
  }

  async calculateStats(id: string): Promise<Schedule> {
    const { data } = await this.client.get(`/schedules/${id}/calculate_stats`);

    return normalizers.getSchedule(data.data, []);
  }

  async getDriversSchedule(
    date: string,
    driverId?: string
  ): Promise<DriversSchedule> {
    const { data } = await this.client.get(
      `/schedules/drivers_schedule?date=${date}${
        driverId ? `&driver_id=${driverId}` : ""
      }`
    );

    return normalizers.getDriversSchedule(data);
  }

  async getDailyProgress(
    date: string,
    driverId: string
  ): Promise<DailyProgress> {
    const { data } = await this.client.get(
      `/schedules/drivers_schedule?date=${date}&driver_id=${driverId}`
    );

    return normalizers.getDailyProgress(data);
  }

  async getScheduleDay(date: string): Promise<ScheduleDay> {
    const { data } = await this.client.get(`/schedule_days/${date}`);
    return normalizers.getScheduleDay(data);
  }

  async lockScheduleDay(date: string): Promise<ScheduleDay> {
    const { data } = await this.client.post(`/schedule_days/${date}/lock`);
    return normalizers.getScheduleDay(data);
  }

  async unlockScheduleDay(date: string): Promise<ScheduleDay> {
    const { data } = await this.client.post(`/schedule_days/${date}/unlock`);
    return normalizers.getScheduleDay(data);
  }

  async getTripTicket(
    id: string,
    scheduleItemIds: string[]
  ): Promise<string[]> {
    const promiseTripTicket = this.client
      .get(`/schedules/${id}/trip_ticket`)
      .then((resp) => resp.data);
    const promises: Promise<any>[] = [promiseTripTicket];

    scheduleItemIds.forEach((scheduleItemId) => {
      const promise = this.client
        .get(`/schedule_items/${scheduleItemId}/invoice`)
        .then((resp) => resp.data);

      promises.push(promise);
    });

    return Promise.all(promises).then((values) => {
      return values;
    });
  }

  async createScheduleItem(form: ScheduleItemForm): Promise<ScheduleItem> {
    const payload = denormalizers.toScheduleItem(form);
    const { data } = await this.client.post("/schedule_items", payload);

    return normalizers.getScheduleItem(data.data);
  }

  async getScheduleItems(scheduleId: string): Promise<ScheduleItem[]> {
    const { data } = await this.client.get(
      `/schedule_items?schedule_id=${scheduleId}`
    );

    return normalizers.getScheduleItems(data.data);
  }

  async updateOrderScheduleItem(
    id: string,
    newOrderNumber: number
  ): Promise<ScheduleItem> {
    const { data } = await this.client.post(
      `/schedule_items/${id}/update_order`,
      { new_order_number: newOrderNumber }
    );

    return normalizers.getScheduleItem(data.data);
  }

  async unassign(id: string): Promise<string> {
    await this.client.delete(`/schedule_items/${id}/unassign`);

    return id;
  }

  async changeSchedule(
    id: string,
    newScheduleId: string
  ): Promise<ScheduleItem> {
    const { data } = await this.client.post(
      `/schedule_items/${id}/change_schedule`,
      { new_schedule_id: newScheduleId }
    );

    return normalizers.getScheduleItem(data.data);
  }

  async setVehicle(params: SetVehicleParams): Promise<Schedule> {
    const { data } = await this.client.post(
      `/schedules/${params.scheduleId}/set_vehicle`,
      {
        vehicle_id: params.vehicleId,
        create_dvir_report: params.createDVIRReport,
      }
    );

    return normalizers.getSchedule(data.data, data.included);
  }

  async getDumpYardTemplates(
    type?: DumpYardTemplateType
  ): Promise<DumpYardTemplate[]> {
    const url = type
      ? `/dump_yard_templates?type=${type}`
      : "/dump_yard_templates";
    const { data } = await this.client.get(url);

    return normalizers.getDumpYardTemplates(data.data);
  }

  async deleteDumpYardTemplate(id: string): Promise<string> {
    await this.client.delete(`/dump_yard_templates/${id}`);

    return id;
  }

  async createDumpYardTemplate(
    form: DumpYardTemplateForm
  ): Promise<DumpYardTemplate> {
    const payload = denormalizers.toDumpYardTemplate(form);
    const { data } = await this.client.post(`/dump_yard_templates`, payload);

    return normalizers.getDumpYardTemplate(data.data);
  }

  async updateDumpYardTemplate(
    template: DumpYardTemplate
  ): Promise<DumpYardTemplate> {
    const payload = denormalizers.toUpdateDumpYardTemplate(template);
    const { data } = await this.client.put(
      `/dump_yard_templates/${template.id}`,
      payload
    );

    return normalizers.getDumpYardTemplate(data.data);
  }

  async getDumpYards(params: GetDumpYardsParams): Promise<DumpYard[]> {
    let url = `/dump_yards?date=${params.date}`;
    if (!!params.status) {
      url += `&status=${params.status}`;
    }

    const { data } = await this.client.get(url);

    return normalizers.getDumpYards(data.data);
  }

  async updatePickupInstructions(
    params: UpdateDumpYardInstructionsParams
  ): Promise<DumpYard> {
    const payload = { instructions: params.instructions };
    const { data } = await this.client.put(
      `/dump_yards/${params.id}/instructions`,
      payload
    );

    return normalizers.getDumpYard(data.data);
  }

  async getOrderStats(): Promise<DashboardOrderStats> {
    const { data } = await this.client.get("dashboard/order_stats");

    return normalizers.getDashboardOrderStats(data);
  }

  async getScheduleStats(): Promise<DashboardScheduleStats> {
    const { data } = await this.client.get("dashboard/schedule_stats");

    return normalizers.getDashboardScheduleStats(data);
  }

  async getInvoices(params: GetInvoicesParams): Promise<Invoice[]> {
    const { data } = await this.client.get(
      `/invoices?order_id=${params.orderId}`
    );

    return normalizers.getInvoices(data.data);
  }

  async getInvoicePDF(id: string): Promise<string> {
    const { data } = await this.client.get(`/invoices/${id}/download`);

    return data;
  }

  async markPaid(params: MarkPaidParams): Promise<boolean> {
    const payload = {
      status: params.status,
      payment_type: params.paymentType,
    };
    await this.client.post(`/invoices/${params.id}/set_status`, payload);

    return true;
  }

  async bulkMarkPaid(params: BulkMarkPaidParams): Promise<boolean> {
    const payload = {
      ids: params.ids,
      payment_type: params.paymentType,
    };
    await this.client.post(`/invoices/bulk_mark_paid`, payload);

    return true;
  }

  async cancelInvoice(params: CancelInvoiceParams): Promise<boolean> {
    const payload = {
      status: InvoiceStatus.CANCELED,
    };
    await this.client.post(`/invoices/${params.id}/set_status`, payload);

    return true;
  }

  async resendInvoice(params: { id: string }): Promise<boolean> {
    await this.client.post(`/invoices/${params.id}/resend`);

    return true;
  }

  // Punch cards
  async getPunchCardSummaries(
    params: GetPunchCardSummariesParams
  ): Promise<PunchCardsForAllDrivers[]> {
    const { data } = await this.client.get(
      `/punch_cards/summaries?from_date=${params.fromDate}&to_date=${params.toDate}`
    );

    return normalizers.getPunchCardSummaries(data);
  }

  async getPunchCardsForDriver(
    params: GetPunchCardsForDriverParams
  ): Promise<PunchCardsForDriver> {
    const { data } = await this.client.get(
      `/punch_cards/get_punch_cards_for_driver?driver_id=${params.driverId}&from_date=${params.fromDate}&to_date=${params.toDate}`
    );

    return normalizers.getPunchCardsForDriver(data);
  }

  async getDriverSafetySummary(
    params: { driverId: string; offset: number }
  ): Promise<DriverSafetySummary> {
    const { data } = await this.client.get(
      `/drivers/${params.driverId}/safety-summary?offset=${params.offset}`
    );

    return normalizers.getDriverSafetySummary(data);
  }

  async getFleetSafetySummary(offset: number): Promise<FleetSafetySummaryResponse> {
    const { data } = await this.client.get(
      `/drivers/safety-summary?offset=${offset}`
    );

    return normalizers.getFleetSafetySummary(data);
  }

  async rescoreSafetyReport(punchCardId: string): Promise<void> {
    await this.client.post(
      `/drivers/safety-reports/${punchCardId}/rescore`,
      {}
    );
  }

  async getTeamSafetySummary(offset: number): Promise<TeamSafetySummary> {
    const { data } = await this.client.get(
      `/drivers/team-safety-summary?offset=${offset}`
    );

    return normalizers.getTeamSafetySummary(data);
  }

  async setTeamSafetyExclusion(
    params: { driverId: string; payPeriodId: string; reason: string }
  ): Promise<void> {
    await this.client.put(
      `/drivers/${params.driverId}/team-safety-exclusion`,
      { pay_period_id: params.payPeriodId, reason: params.reason }
    );
  }

  async clearTeamSafetyExclusion(
    params: { driverId: string; payPeriodId: string }
  ): Promise<void> {
    await this.client.delete(
      `/drivers/${params.driverId}/team-safety-exclusion?pay_period_id=${params.payPeriodId}`
    );
  }

  async lockTeamSafetyPeriod(payPeriodId: string): Promise<void> {
    await this.client.post(`/drivers/team-safety-summary/lock`, {
      pay_period_id: payPeriodId,
    });
  }

  async unlockTeamSafetyPeriod(payPeriodId: string): Promise<void> {
    await this.client.delete(
      `/drivers/team-safety-summary/lock?pay_period_id=${payPeriodId}`
    );
  }

  async updatePunchCard(params: UpdatePunchCardsParams): Promise<PunchCard> {
    const payload = denormalizers.toPunchCard(params);
    const { data } = await this.client.put(
      `/punch_cards/${params.id}`,
      payload
    );

    return normalizers.getPunchCard(data.data);
  }

  async createPunchCard(params: CreatePunchCardParams): Promise<PunchCard> {
    const payload = {
      driver_id: params.driverId,
      date: params.date,
      punch_in: params.punchIn,
      punch_out: params.punchOut,
    };
    const { data } = await this.client.post(`/punch_cards/create`, payload);

    return normalizers.getPunchCard(data.data);
  }

  async deletePunchCard(id: string): Promise<string> {
    await this.client.delete(`/punch_cards/${id}`);
    return id;
  }

  // Pay Periods
  async getCurrentPayPeriod(): Promise<PayPeriodResponse> {
    const { data } = await this.client.get(`/pay_periods`);
    return normalizers.getPayPeriodResponse(data);
  }

  async getPayPeriodById(id: string): Promise<PayPeriodResponse> {
    const { data } = await this.client.get(`/pay_periods/${id}`);
    return normalizers.getPayPeriodResponse(data);
  }

  async getPayPeriodSummaries(id: string): Promise<PayPeriodSummaryResponse> {
    const { data } = await this.client.get(`/pay_periods/${id}/summaries`);
    return normalizers.getPayPeriodSummaryResponse(data);
  }

  // Reports
  async getDailyReport(date: string): Promise<DailyReport> {
    const { data } = await this.client.post("/reports/daily", { date });

    return normalizers.getDailyReport(data);
  }

  async getDriverDailyReport(
    params: GetDailyDriverReportParams
  ): Promise<DailyDriverReport> {
    const payload = {
      date: params.date,
      driver_id: params.driverId,
    };
    const { data } = await this.client.post(
      "/reports/daily_for_driver",
      payload
    );

    return normalizers.getDriverDailyReport(data);
  }

  async getYearlyReport(year: string): Promise<YearlyReport> {
    const { data } = await this.client.post("/reports/yearly", { year });

    return normalizers.getYearlyReport(data);
  }

  async getProfitReport(params: GetProfitReportParams): Promise<ProfitReport> {
    const { data } = await this.client.post("/reports/profit", {
      from_date: params.fromDate,
      to_date: params.toDate,
    });

    return normalizers.getProfitReport(data);
  }

  async getOwesReport(): Promise<OwesReport> {
    const { data } = await this.client.post("/reports/owes");

    return normalizers.getOwesReport(data);
  }

  async getHGEOwesReport(
    params: GetHGEOwesReportParams
  ): Promise<HGEOwesReport> {
    const { data } = await this.client.post("/reports/hge_owes", {
      from_date: params.fromDate,
      to_date: params.toDate,
    });

    return normalizers.getHGEOwesReport(data);
  }

  async getSmartRoofOwesReport(
    params: GetSmartRoofOwesReportParams
  ): Promise<SmartRoofOwesReport> {
    const { data } = await this.client.post("/reports/smartroof_owes", {
      from_date: params.fromDate,
      to_date: params.toDate,
    });

    return normalizers.getSmartRoofOwesReport(data);
  }

  // Invoices
  async createWeightOverageInvoice(
    params: CreateWeightOverageInvoiceParams
  ): Promise<boolean> {
    const payload = denormalizers.toCreateWeightOverageInvoicePayload(params);
    await this.client.post(
      `orders/${params.orderId}/add_weight_overage`,
      payload
    );

    return true;
  }

  async createCustomInvoice(
    params: CreateCustomInvoiceParams
  ): Promise<boolean> {
    const payload = denormalizers.toCustomInvoicePayload(params);
    await this.client.post(
      `orders/${params.orderId}/add_custom_invoice`,
      payload
    );

    return true;
  }

  // Dumpsters
  async getDumpsters(params: GetDumpstersParams): Promise<Dumpster[]> {
    const query = new URLSearchParams();
    if (params?.dumpsterSize) {
      query.set("dumpster_size", params.dumpsterSize);
    }
    if (params?.withCoordinates) {
      query.set("with_coordinates", "true");
    }
    const qs = query.toString();
    const url = qs ? `/dumpsters?${qs}` : "/dumpsters";

    const { data } = await this.client.get(url);

    return normalizers.getDumpsters(data.data);
  }

  async getDumpster(id: string): Promise<Dumpster> {
    const { data } = await this.client.get(`/dumpsters/${id}`);

    return normalizers.getDumpster(data.data);
  }

  async createDumpster(form: DumpsterForm): Promise<Dumpster> {
    const payload = denormalizers.toDumpster(form);
    const { data } = await this.client.post(`/dumpsters`, payload);

    return normalizers.getDumpster(data.data);
  }

  async updateDumpster(dumpster: Dumpster): Promise<Dumpster> {
    const payload = denormalizers.toUpdateDumpster(dumpster);
    const { data } = await this.client.put(
      `/dumpsters/${dumpster.id}`,
      payload
    );

    return normalizers.getDumpster(data.data);
  }

  async deleteDumpster(id: string): Promise<string> {
    await this.client.delete(`/dumpsters/${id}`);

    return id;
  }

  async setDumpsterLocation(
    params: SetDumpsterLocationParams
  ): Promise<Dumpster> {
    const { data } = await this.client.post(
      `/dumpsters/${params.dumpsterId}/set_location`,
      {
        location_type: params.locationType,
        order_id: params.orderId || "",
        storage_yard_id: params.storageYardId || "",
        address: params.address,
        city: params.city,
        zip: params.zip,
      }
    );

    return normalizers.getDumpster(data.data);
  }

  async getDumpsterLocationHistory(
    dumpsterId: string
  ): Promise<DumpsterLocationHistory[]> {
    const { data } = await this.client.get(
      `/dumpsters/${dumpsterId}/location_history`
    );

    return normalizers.getDumpsterLocationHistoryList(data.data);
  }

  // Vehicles

  async getVehicles(withStats = false): Promise<Vehicle[]> {
    const url = withStats ? "/vehicles?with_stats=true" : "/vehicles";

    const { data } = await this.client.get(url);

    return normalizers.getVehicles(data.data);
  }

  async getVehicle(id: string): Promise<Vehicle> {
    const { data } = await this.client.get(`/vehicles/${id}`);

    return normalizers.getVehicle(data.data);
  }

  async createVehicle(form: VehicleForm): Promise<Vehicle> {
    const payload = denormalizers.toVehicle(form);
    const { data } = await this.client.post(`/vehicles`, payload);

    return normalizers.getVehicle(data.data);
  }

  async updateVehicle(vehicle: Vehicle): Promise<Vehicle> {
    const payload = denormalizers.toUpdateVehicle(vehicle);
    const { data } = await this.client.put(`/vehicles/${vehicle.id}`, payload);

    return normalizers.getVehicle(data.data);
  }

  async deleteVehicle(id: string): Promise<string> {
    await this.client.delete(`/vehicles/${id}`);

    return id;
  }

  // API Keys
  async getApiKeys(): Promise<ApiKey[]> {
    const { data } = await this.client.get("/api_keys");
    return normalizers.getApiKeys(data);
  }

  async createApiKey(req: CreateApiKeyRequest): Promise<CreatedApiKey> {
    const { data } = await this.client.post("/api_keys", req);
    return normalizers.getCreatedApiKey(data);
  }

  async updateApiKey(id: string, req: UpdateApiKeyRequest): Promise<ApiKey> {
    const { data } = await this.client.put(`/api_keys/${id}`, req);
    return normalizers.getApiKey(data);
  }

  async revokeApiKey(id: string): Promise<string> {
    await this.client.delete(`/api_keys/${id}`);
    return id;
  }

  // Settings
  async getSetting<T>(key: string): Promise<{ unit: string; value: T }> {
    const { data } = await this.client.get(`/settings/${key}`);
    return data as { unit: string; value: T };
  }

  async putSetting<T>(key: string, value: T): Promise<{ unit: string; value: T }> {
    const { data } = await this.client.put(`/settings/${key}`, value);
    return data as { unit: string; value: T };
  }

  async getPricing(): Promise<Pricing> {
    const { value } = await this.getSetting<normalizers.RawPricing>("pricing");
    return normalizers.getPricing(value);
  }

  async updatePricing(pricing: Pricing): Promise<Pricing> {
    const { value } = await this.putSetting("pricing", denormalizers.pricing(pricing));
    return normalizers.getPricing(value);
  }

  // Service Time Per Stop
  async getServiceTime(): Promise<ServiceTimePerStop> {
    const { value } = await this.getSetting<normalizers.RawServiceTime>(
      "service_time_per_stop"
    );
    return normalizers.getServiceTime(value);
  }

  async updateServiceTime(
    serviceTime: ServiceTimePerStop
  ): Promise<ServiceTimePerStop> {
    const { value } = await this.putSetting(
      "service_time_per_stop",
      denormalizers.toServiceTime(serviceTime)
    );
    return normalizers.getServiceTime(value);
  }

  // Dump Weight by Material
  async getDumpWeightByMaterial(): Promise<DumpWeightByMaterial> {
    const { value } = await this.getSetting<normalizers.RawDumpWeightByMaterial>(
      "dump_weight_by_material"
    );
    return normalizers.getDumpWeightByMaterial(value);
  }

  async updateDumpWeightByMaterial(
    weights: DumpWeightByMaterial
  ): Promise<DumpWeightByMaterial> {
    const { value } = await this.putSetting(
      "dump_weight_by_material",
      denormalizers.dumpWeightByMaterial(weights)
    );
    return normalizers.getDumpWeightByMaterial(value);
  }

  // Late Punch-out Threshold (payroll). Bare integer minutes — no normalizer.
  async getLatePunchoutThreshold(): Promise<number> {
    const { value } = await this.getSetting<number>(
      "late_punchout_threshold_minutes"
    );
    return value;
  }

  async updateLatePunchoutThreshold(minutes: number): Promise<number> {
    const { value } = await this.putSetting<number>(
      "late_punchout_threshold_minutes",
      minutes
    );
    return value;
  }

  // Daily Work-Time Cap (route optimizer). Bare number of HOURS — the backend
  // stores hours (daily_work_time_cap_hours), so no unit conversion.
  async getDailyWorkTimeCap(): Promise<number> {
    const { value } = await this.getSetting<number>(
      "daily_work_time_cap_hours"
    );
    return value;
  }

  async updateDailyWorkTimeCap(hours: number): Promise<number> {
    const { value } = await this.putSetting<number>(
      "daily_work_time_cap_hours",
      hours
    );
    return value;
  }

  // Delivery photo required (driver app). Plain boolean.
  async getDeliveryPhotoRequired(): Promise<boolean> {
    const { value } = await this.getSetting<boolean>(
      "delivery_photo_required"
    );
    return value;
  }

  async updateDeliveryPhotoRequired(enabled: boolean): Promise<boolean> {
    const { value } = await this.putSetting<boolean>(
      "delivery_photo_required",
      enabled
    );
    return value;
  }

  // Dump receipt required (driver app). Plain boolean.
  async getDumpReceiptRequired(): Promise<boolean> {
    const { value } = await this.getSetting<boolean>(
      "dump_receipt_required"
    );
    return value;
  }

  async updateDumpReceiptRequired(enabled: boolean): Promise<boolean> {
    const { value } = await this.putSetting<boolean>(
      "dump_receipt_required",
      enabled
    );
    return value;
  }

  // Pickup photo required (driver app). Plain boolean.
  async getPickupPhotoRequired(): Promise<boolean> {
    const { value } = await this.getSetting<boolean>(
      "pickup_photo_required"
    );
    return value;
  }

  async updatePickupPhotoRequired(enabled: boolean): Promise<boolean> {
    const { value } = await this.putSetting<boolean>(
      "pickup_photo_required",
      enabled
    );
    return value;
  }

  // Engine cool-down (turbo) threshold. The backend stores seconds
  // (turbo_cooldown_min_seconds); the web edits whole minutes, so convert here.
  async getTurboCooldownThreshold(): Promise<number> {
    const { value } = await this.getSetting<number>(
      "turbo_cooldown_min_seconds"
    );
    return Math.round(value / 60);
  }

  async updateTurboCooldownThreshold(minutes: number): Promise<number> {
    const { value } = await this.putSetting<number>(
      "turbo_cooldown_min_seconds",
      minutes * 60
    );
    return Math.round(value / 60);
  }

  // DVIR Questions
  async getDVIRQuestions(
    params?: GetDVIRQuestionsParams
  ): Promise<DVIRQuestion[]> {
    let url = "/dvir_questions";
    const queryParams = new URLSearchParams();

    if (params?.templateId) {
      queryParams.append("dvir_template_id", params.templateId);
    }
    if (params?.questionType) {
      queryParams.append("question_type", params.questionType);
    }

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const { data } = await this.client.get(url);
    return normalizers.getDVIRQuestions(data.data);
  }

  async getDVIRQuestion(id: string): Promise<DVIRQuestion> {
    const { data } = await this.client.get(`/dvir_questions/${id}`);
    return normalizers.getDVIRQuestion(data.data);
  }

  async createDVIRQuestion(form: DVIRQuestionForm): Promise<DVIRQuestion> {
    const payload = denormalizers.toDVIRQuestion(form);
    const { data } = await this.client.post("/dvir_questions", payload);
    return normalizers.getDVIRQuestion(data.data);
  }

  async updateDVIRQuestion(question: DVIRQuestion): Promise<DVIRQuestion> {
    const payload = denormalizers.toUpdateDVIRQuestion(question);
    const { data } = await this.client.put(
      `/dvir_questions/${question.id}`,
      payload
    );
    return normalizers.getDVIRQuestion(data.data);
  }

  async deleteDVIRQuestion(id: string): Promise<string> {
    await this.client.delete(`/dvir_questions/${id}`);
    return id;
  }

  // DVIR Templates
  async getDVIRTemplates(): Promise<DVIRTemplate[]> {
    const { data } = await this.client.get("/dvir_templates");
    return normalizers.getDVIRTemplates(data.data);
  }

  async getDVIRTemplate(id: string): Promise<DVIRTemplate> {
    const { data } = await this.client.get(`/dvir_templates/${id}`);
    return normalizers.getDVIRTemplate(data.data, data.included);
  }

  async createDVIRTemplate(form: DVIRTemplateForm): Promise<DVIRTemplate> {
    const payload = denormalizers.toDVIRTemplate(form);
    const { data } = await this.client.post("/dvir_templates", payload);
    return normalizers.getDVIRTemplate(data.data);
  }

  async updateDVIRTemplate(template: DVIRTemplate): Promise<DVIRTemplate> {
    const payload = denormalizers.toUpdateDVIRTemplate(template);
    const { data } = await this.client.put(
      `/dvir_templates/${template.id}`,
      payload
    );
    return normalizers.getDVIRTemplate(data.data, data.included);
  }

  async deleteDVIRTemplate(id: string): Promise<string> {
    await this.client.delete(`/dvir_templates/${id}`);
    return id;
  }

  // DVIR Template Questions
  async addQuestionToTemplate(
    params: AddQuestionToTemplateParams
  ): Promise<boolean> {
    const payload = {
      question_id: params.questionId,
      is_required: params.isRequired,
    };
    await this.client.post(
      `/dvir_templates/${params.templateId}/add_question`,
      payload
    );
    return true;
  }

  async removeQuestionFromTemplate(
    params: RemoveQuestionFromTemplateParams
  ): Promise<boolean> {
    const payload = {
      template_question_id: params.questionId,
    };
    await this.client.post(
      `/dvir_templates/${params.templateId}/remove_question`,
      payload
    );
    return true;
  }

  async changeQuestionOrder(
    params: ChangeQuestionOrderParams
  ): Promise<boolean> {
    const payload = {
      template_question_id: params.questionId,
      new_order_position: params.newOrderPosition,
    };
    await this.client.post(
      `/dvir_templates/${params.templateId}/change_question_order`,
      payload
    );
    return true;
  }

  // DVIR Reports
  async getDVIRReports(params?: GetDVIRReportsParams): Promise<DVIRReport[]> {
    let url = "/dvir_reports";
    const queryParams = new URLSearchParams();

    // Pagination defaults
    queryParams.append("count", String(params?.count ?? 20));
    queryParams.append("offset", String(params?.offset ?? 0));

    if (params?.driverId) {
      queryParams.append("driver_id", params.driverId);
    }
    if (params?.vehicleId) {
      queryParams.append("vehicle_id", params.vehicleId);
    }
    if (params?.templateId) {
      queryParams.append("template_id", params.templateId);
    }
    if (params?.status) {
      queryParams.append("status", params.status);
    }
    if (params?.dateFrom) {
      queryParams.append("date_from", params.dateFrom);
    }
    if (params?.dateTo) {
      queryParams.append("date_to", params.dateTo);
    }
    if (params?.date) {
      queryParams.append("date", params.date);
    }

    url += `?${queryParams.toString()}`;

    const { data } = await this.client.get(url);
    return normalizers.getDVIRReports(data.data);
  }

  async getDVIRReport(id: string): Promise<DVIRReport> {
    const { data } = await this.client.get(`/dvir_reports/${id}`);
    return normalizers.getDVIRReport(data.data);
  }

  async createDVIRReport(params: CreateDVIRReportParams): Promise<DVIRReport> {
    const payload = denormalizers.toDVIRReport(params);
    const { data } = await this.client.post("/dvir_reports", payload);
    return normalizers.getDVIRReport(data.data);
  }

  async deleteDVIRReport(id: string): Promise<string> {
    await this.client.delete(`/dvir_reports/${id}`);
    return id;
  }

  // Fleet Optimizations
  async createFleetOptimization(
    input: FleetOptimizationInput
  ): Promise<{ id: string; status: FleetOptimizationStatus }> {
    const payload: Record<string, any> = {
      date: input.date,
      driver_assignments: input.driverAssignments.map((a) => {
        const assignment: Record<string, any> = {
          driver_id: a.driverId,
          vehicle_id: a.vehicleId,
        };
        if (a.startingDumpster) {
          assignment.starting_dumpster = {
            size: a.startingDumpster.size,
            state: a.startingDumpster.state,
          };
        }
        if (a.startTime) assignment.start_time = a.startTime;
        if (a.endTime) assignment.end_time = a.endTime;
        return assignment;
      }),
    };
    // Optional optimizer tuning — send only what was set (camel→snake).
    if (input.endTimePenalty != null)
      payload.end_time_penalty = input.endTimePenalty;
    if (input.dumpFeeRankWeight != null && input.dumpFeeRankWeight !== 1.0)
      payload.dump_fee_rank_weight = input.dumpFeeRankWeight;
    if (input.compactnessPenalty != null)
      payload.compactness_penalty = input.compactnessPenalty;
    if (input.maintenancePerMile != null)
      payload.maintenance_per_mile = input.maintenancePerMile;
    if (input.timeWindowPenalty != null)
      payload.time_window_penalty = input.timeWindowPenalty;
    if (input.timeWindows?.length)
      payload.time_windows = input.timeWindows.map((w) => ({
        job_id: w.jobId,
        start: w.start,
        end: w.end,
      }));
    if (input.overtimeAllowed != null)
      payload.overtime_allowed = input.overtimeAllowed;
    if (input.overtimePenalty != null)
      payload.overtime_penalty = input.overtimePenalty;
    if (input.allowCarryover != null)
      payload.allow_carryover = input.allowCarryover;
    if (input.carryoverThreshold != null)
      payload.carryover_threshold = input.carryoverThreshold;
    if (input.maxCarryovers != null)
      payload.max_carryovers = input.maxCarryovers;
    if (input.balancePenalty != null)
      payload.balance_penalty = input.balancePenalty;
    if (input.pickupWasteTypes?.length)
      payload.pickup_waste_types = input.pickupWasteTypes.map((w) => ({
        pickup_id: w.pickupId,
        waste_type: w.wasteType,
      }));
    if (input.pickupDumpWeights?.length)
      payload.pickup_dump_weights = input.pickupDumpWeights.map((w) => ({
        pickup_id: w.pickupId,
        weight: w.weight,
      }));
    if (input.pickupDumpYards?.length)
      payload.pickup_dump_yards = input.pickupDumpYards.map((y) => ({
        pickup_id: y.pickupId,
        dump_yard_id: y.dumpYardId,
      }));
    if (input.locks?.length)
      payload.locks = input.locks.map((l) => ({
        job_id: l.jobId,
        driver_id: l.driverId,
      }));
    if (input.exclude?.length) payload.exclude = input.exclude;
    if (input.allowTopoffForJobs?.length)
      payload.allow_topoff_for_jobs = input.allowTopoffForJobs;
    if (input.precedences?.length)
      payload.precedences = input.precedences.map((p) => ({
        after_job_id: p.afterJobId,
        before_job_id: p.beforeJobId,
      }));
    if (input.swaps?.length)
      payload.swaps = input.swaps.map((s) => ({
        pickup_job_id: s.pickupJobId,
        delivery_job_id: s.deliveryJobId,
      }));
    const { data } = await this.client.post("/fleet_optimizations", payload);
    return { id: data.id, status: data.status };
  }

  async getFleetOptimization(id: string): Promise<FleetOptimization> {
    const { data } = await this.client.get(`/fleet_optimizations/${id}`);
    return normalizers.getFleetOptimization(data);
  }

  // Apply a ranked plan of a completed run to the real schedule for its date.
  // Replaces existing schedule items for those drivers. dryRun previews without
  // writing. planIndex 0 = the best (cheapest) option.
  async acceptFleetOptimization(
    id: string,
    planIndex: number,
    dryRun = false
  ): Promise<AcceptResult> {
    const { data } = await this.client.post(
      `/fleet_optimizations/${id}/accept`,
      { plan_index: planIndex, dry_run: dryRun }
    );
    return normalizers.getAcceptResult(data);
  }

  async listFleetOptimizations(
    date: string,
    limit?: number,
    offset?: number
  ): Promise<FleetOptimization[]> {
    const params: Record<string, string | number> = { date };
    if (limit != null) params.limit = limit;
    if (offset != null) params.offset = offset;
    const { data } = await this.client.get("/fleet_optimizations", { params });
    // The endpoint returns a bare array; tolerate a { runs: [...] } envelope too.
    const runs = Array.isArray(data) ? data : data?.runs ?? [];
    return runs.map(normalizers.getFleetOptimization);
  }

  async compareFleetOptimization(id: string): Promise<ScheduleComparison> {
    const { data } = await this.client.post(`/fleet_optimizations/${id}/compare`);
    return normalizers.getScheduleComparison(data);
  }

  async listComparisons(id: string): Promise<ScheduleComparison[]> {
    const { data } = await this.client.get(`/fleet_optimizations/${id}/comparisons`);
    return normalizers.getScheduleComparisons(data);
  }

  async clearComparisons(id: string): Promise<void> {
    await this.client.delete(`/fleet_optimizations/${id}/comparisons`);
  }

  // Customers
  async getCustomers(params: { search?: string; offset: number; count: number; sort?: string }): Promise<Customer[]> {
    const qs = new URLSearchParams({ count: params.count + "", offset: params.offset + "" });
    if (params.search) qs.append("search", params.search);
    if (params.sort) qs.append("sort", params.sort);
    const { data } = await this.client.get(`/customers?${qs.toString()}`);
    return normalizers.getCustomers(data.data);
  }

  async getCustomer(id: string): Promise<Customer> {
    const { data } = await this.client.get(`/customers/${id}`);
    return normalizers.getCustomer(data.data);
  }

  async toggleCustomerFavorite(id: string, isFavorite: boolean): Promise<Customer> {
    const { data } = await this.client.put(`/customers/${id}`, {
      data: { type: "customers", id, attributes: { is_favorite: isFavorite } },
    });
    return normalizers.getCustomer(data.data);
  }

  // Dump Events

  async getDumpEvents(
    filters: DumpEventFilters,
    pagination?: TriplePPagination
  ): Promise<DumpEvent[]> {
    const params = normalizers.buildDumpEventParams(filters, pagination);
    const { data } = await this.client.get(`/dump_events?${params.toString()}`);
    return normalizers.getDumpEvents(data.data);
  }

  async getDumpEventSummary(filters: DumpEventFilters): Promise<RangeSummary> {
    const params = normalizers.buildDumpEventParams(filters);
    params.append("summary", "1");
    const { data } = await this.client.get(`/dump_events?${params.toString()}`);
    return normalizers.getRangeSummary(data);
  }

  async getDumpEventReceipt(id: string): Promise<string> {
    const { data } = await this.client.get(`/dump_events/${id}/receipt`);
    // The API returns a base64-encoded JPEG via API Gateway's base64 response.
    // axios may hand us a raw string (image/* content type) or, if it parsed the
    // body as JSON, an object with a `body` field. Wrap it in a data URL so it can
    // be used directly as an <img src>.
    const base64 = typeof data === "string" ? data : data?.body ?? "";
    if (!base64) {
      throw new Error(
        `getDumpEventReceipt: empty receipt body for dump event ${id}`
      );
    }
    return `data:image/jpeg;base64,${base64}`;
  }

  // Manual admin correction via PATCH /dump_events/{id} (the review handler).
  // Only provided fields are sent; decimals go as strings to avoid float drift.
  async updateDumpEvent(id: string, update: DumpEventUpdate): Promise<void> {
    const body: Record<string, unknown> = {};
    if (update.paidAmount != null) body.paid_amount = String(update.paidAmount);
    if (update.weight != null) body.weight = String(update.weight);
    if (update.confirm) body.extraction_status = "confirmed";
    // Reattribute the receipt to a different order (base58 id as-is).
    if (update.orderId != null) body.order_id = update.orderId;
    await this.client.patch(`/dump_events/${id}`, body);
  }

  // Receipts

  async getReceipts(
    filters: ReceiptFilters,
    pagination?: TriplePPagination
  ): Promise<Receipt[]> {
    const params = normalizers.buildReceiptParams(filters, pagination);
    const { data } = await this.client.get(`/receipts?${params.toString()}`);
    return normalizers.getReceipts(data.data);
  }

  async getReceiptSummary(filters: ReceiptFilters): Promise<RangeSummary> {
    const params = normalizers.buildReceiptParams(filters);
    params.append("summary", "1");
    const { data } = await this.client.get(`/receipts?${params.toString()}`);
    return normalizers.getRangeSummary(data);
  }

  async getReceiptImage(id: string): Promise<string> {
    const { data } = await this.client.get(`/receipts/${id}/receipt`);
    // The API returns a base64-encoded JPEG via API Gateway's base64 response.
    // axios may hand us a raw string (image/* content type) or, if it parsed the
    // body as JSON, an object with a `body` field. Wrap it in a data URL so it can
    // be used directly as an <img src>.
    const base64 = typeof data === "string" ? data : data?.body ?? "";
    if (!base64) {
      throw new Error(
        `getReceiptImage: empty receipt body for receipt ${id}`
      );
    }
    return `data:image/jpeg;base64,${base64}`;
  }

  // Partial update via PATCH /receipts/{id}. Only provided fields are sent;
  // decimals go as strings to avoid float drift.
  async updateReceipt(id: string, update: ReceiptUpdate): Promise<void> {
    const body: Record<string, unknown> = {};
    if (update.cost != null) body.cost = String(update.cost);
    if (update.receiptType != null) body.receipt_type = update.receiptType;
    if (update.vehicleId != null) body.vehicle_id = update.vehicleId;
    if (update.receiptDate != null) body.receipt_date = update.receiptDate;
    if (update.note != null) body.note = update.note;
    await this.client.patch(`/receipts/${id}`, body);
  }

  async deleteReceipt(id: string): Promise<string> {
    await this.client.delete(`/receipts/${id}`);
    return id;
  }

  // Invoice Suggestions

  async getInvoiceSuggestions(
    status?: SuggestionStatus
  ): Promise<InvoiceSuggestion[]> {
    const { data } = await this.client.get(
      `/invoice_suggestions?status=${status ?? "pending"}`
    );
    return normalizers.getInvoiceSuggestions(data.data);
  }

  async approveInvoiceSuggestion(
    id: string,
    body: ApproveSuggestionBody
  ): Promise<void> {
    await this.client.post(`/invoice_suggestions/${id}/approve`, body);
    return;
  }

  async rejectInvoiceSuggestion(id: string): Promise<void> {
    await this.client.post(`/invoice_suggestions/${id}/reject`, {});
    return;
  }

  async getPublicExtraRentalDayPrice(): Promise<number> {
    const { data } = await this.client.get('/orders/public/pricing');
    return data.extra_rental_day_price;
  }

  // Public pricing + closed days in the one call the order form makes on mount.
  async getPublicPricing(): Promise<{ extraDayRate: number; closedDates: string[] }> {
    const { data } = await this.client.get('/orders/public/pricing');
    return {
      extraDayRate: data.extra_rental_day_price,
      closedDates: Array.isArray(data.closed_dates) ? data.closed_dates : [],
    };
  }

  // Closed days (admin-managed) CRUD. The orders service is mounted under the
  // `/orders` base path on the API custom domain, so these routes are
  // `/orders/closed-days` (the serverless path is `/closed-days`; the base
  // path is prepended by the custom domain mapping).
  async getClosedDays(
    filter: "upcoming" | "past" | "all" = "upcoming"
  ): Promise<ClosedDay[]> {
    const { data } = await this.client.get(`/orders/closed-days?filter=${filter}`);
    return normalizers.getClosedDays(data.data);
  }

  async createClosedDay(date: string, reason: string): Promise<ClosedDay> {
    const payload = denormalizers.toClosedDay(date, reason);
    const { data } = await this.client.post('/orders/closed-days', payload);
    return normalizers.getClosedDay(data.data);
  }

  async deleteClosedDay(id: string): Promise<string> {
    await this.client.delete(`/orders/closed-days/${id}`);
    return id;
  }

  // AI Chat
  async sendChatMessage(req: { conversationId?: string; message: string; model?: string }): Promise<{ conversationId: string; messageId: string }> {
    const body: Record<string, unknown> = { message: req.message };
    if (req.conversationId != null) body.conversation_id = req.conversationId;
    if (req.model != null) body.model = req.model;
    const { data } = await this.client.post("/ai_chat", body);
    return { conversationId: data.conversation_id, messageId: data.message_id };
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    const { data } = await this.client.get(`/ai_chat/${conversationId}`);
    return {
      conversationId: data.conversation_id,
      messages: (data.messages ?? []).map((m: any) => ({
        id: m.id,
        role: m.role,
        status: m.status,
        content: m.content,
        toolsUsed: m.tools_used as string[] | undefined,
      })),
      costUsd: data.cost_usd != null ? String(data.cost_usd) : undefined,
    };
  }

  async listConversations(): Promise<ConversationSummary[]> {
    const { data } = await this.client.get("/ai_chat");
    return (data.conversations ?? []).map((c: any) => ({
      id: c.id,
      title: c.title,
      updatedAt: c.updated_at,
    }));
  }

  async listKnowledgeNotes(status?: KnowledgeNoteStatus): Promise<KnowledgeNote[]> {
    const url = status ? `/ai_chat/knowledge?status=${status}` : "/ai_chat/knowledge";
    const { data } = await this.client.get(url);
    return (data.notes ?? []).map((n: any): KnowledgeNote => ({
      id: n.id,
      content: n.content,
      status: n.status,
      sourceConversationId: n.source_conversation_id ?? undefined,
      createdAt: n.created_at,
      updatedAt: n.updated_at,
    }));
  }

  async updateKnowledgeNote(
    id: string,
    update: { status?: KnowledgeNoteStatus; content?: string }
  ): Promise<KnowledgeNote> {
    const body: Record<string, unknown> = {};
    if (update.status != null) body.status = update.status;
    if (update.content != null) body.content = update.content;
    const { data } = await this.client.put(`/ai_chat/knowledge/${id}`, body);
    return {
      id: data.id,
      content: data.content,
      status: data.status,
      sourceConversationId: data.source_conversation_id ?? undefined,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  // AI Chat actions

  private mapAction(a: any): ChatAction {
    return {
      id: a.id,
      conversationId: a.conversation_id,
      messageId: a.message_id ?? undefined,
      tool: a.tool,
      op: a.op,
      args: a.args,
      preview: a.preview,
      status: a.status,
      result: a.result ?? undefined,
      confirmedBy: a.confirmed_by ?? undefined,
      createdAt: a.created_at,
      updatedAt: a.updated_at,
    };
  }

  async listActions(conversationId: string): Promise<ChatAction[]> {
    const { data } = await this.client.get(`/ai_chat/actions?conversation_id=${conversationId}`);
    return (data.actions ?? []).map((a: any) => this.mapAction(a));
  }

  async confirmAction(id: string): Promise<ChatAction> {
    const { data } = await this.client.post(`/ai_chat/actions/${id}/confirm`);
    return this.mapAction(data);
  }

  async cancelAction(id: string): Promise<ChatAction> {
    const { data } = await this.client.post(`/ai_chat/actions/${id}/cancel`);
    return this.mapAction(data);
  }

  // Driver Documents (admin-only)

  async getDriverDocuments(driverId: string): Promise<DriverDocument[]> {
    const { data } = await this.client.get(`/drivers/${driverId}/documents`);
    return normalizers.getDriverDocuments(data.documents ?? []);
  }

  async uploadDriverDocument(
    driverId: string,
    req: UploadDriverDocumentRequest
  ): Promise<DriverDocument> {
    const payload = {
      category: req.category,
      label: req.label,
      file: req.fileBase64,
      content_type: req.contentType,
    };
    const { data } = await this.client.post(
      `/drivers/${driverId}/documents`,
      payload
    );
    return normalizers.getDriverDocument(data);
  }

  // Returns a full data:<contentType>;base64,... URL.
  // The caller must pass the document's stored contentType (from the list row)
  // so the prefix is correct for both images (image/jpeg, image/png) and PDFs
  // (application/pdf). This mirrors getReceiptImage's handling of the
  // API-Gateway {body} quirk, but uses the caller-supplied content type rather
  // than hardcoding image/jpeg.
  async getDriverDocumentImage(
    driverId: string,
    documentId: string,
    contentType: string
  ): Promise<string> {
    const { data } = await this.client.get(
      `/drivers/${driverId}/documents/${documentId}`
    );
    const base64 = typeof data === "string" ? data : data?.body ?? "";
    if (!base64) {
      throw new Error(
        `getDriverDocumentImage: empty body for document ${documentId}`
      );
    }
    return `data:${contentType};base64,${base64}`;
  }

  async deleteDriverDocument(
    driverId: string,
    documentId: string
  ): Promise<void> {
    await this.client.delete(`/drivers/${driverId}/documents/${documentId}`);
  }
}
